import React, { useEffect, useState, useCallback } from 'react';
import BusinessSection from '../components/InvoiceForm/BusinessSection';
import CustomerSection from '../components/InvoiceForm/CustomerSection';
import LineItems from '../components/InvoiceForm/LineItems';
import TaxSummary from '../components/InvoiceForm/TaxSummary';
import AdditionalFields from '../components/InvoiceForm/AdditionalFields';
import ActionButtons from '../components/InvoiceForm/ActionButtons';
import InvoicePreview from '../components/InvoicePreview';
import { useInvoice } from '../hooks/useInvoice';
import { local } from '../lib/storage';
import { apiClient } from '../lib/api';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Maximize2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export default function Generator({ presetCustomer }) {
  const { user, plan, usage, refresh } = useAuth();
  const { t } = useLanguage();
  const invoiceState = useInvoice();
  const {
    business, setBusiness,
    customer, setCustomer,
    lineItems, addLineItem, addMultipleLineItems, removeLineItem, removeLineItems, duplicateLineItem, updateLineItem,
    notes, setNotes, number, setNumber,
    date, setDate, dueDate, setDueDate,
    paymentTerms, setPaymentTerms, customPaymentTerms, setCustomPaymentTerms,
    invoice, reset,
  } = invoiceState;

  const previewInvoice = useDebounced(invoice, 200);
  const [mobilePreview, setMobilePreview] = useState(false);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [accountInvoiceCount, setAccountInvoiceCount] = useState(null);

  const [savedCustomers, setSavedCustomers] = useState(() => local.getCustomers());
  const [savedItems, setSavedItems] = useState(() => local.getItems());

  useEffect(() => {
    if (user && user !== false) refresh();
  }, [user?.id, refresh]);

  useEffect(() => {
    if (!user || user === false) {
      setAccountInvoiceCount(null);
      return;
    }
    (async () => {
      try {
        const { data } = await apiClient.get('/invoices');
        setAccountInvoiceCount(Array.isArray(data) ? data.length : null);
      } catch {
        setAccountInvoiceCount(null);
      }
    })();
  }, [user?.id]);

  useEffect(() => {
    if (!user || user === false || plan === 'free') return;
    (async () => {
      try {
        const { data } = await apiClient.get('/businesses');
        if (data?.[0]) setBusiness({ ...data[0], bankDetails: data[0].bankDetails || {} });
      } catch {}
    })();
  }, [user, plan, setBusiness]);

  useEffect(() => {
    if (presetCustomer && !customer.state) {
      setCustomer((c) => ({ ...c, state: presetCustomer.state, stateName: presetCustomer.stateName }));
    }
  }, [presetCustomer]);

  const handleSaveCustomer = useCallback((c) => {
    if (!c.name) return;
    const next = local.addCustomer(c);
    setSavedCustomers(next);
  }, []);

  const handleSaved = useCallback(() => {
    setNumber(local.nextInvoiceNumber());
  }, [setNumber]);

  const usedInvoices = Math.max(Number(usage?.invoices || 0), Number(accountInvoiceCount || 0));
  const invoiceLimitReached = user && user !== false && usage?.limit != null && usedInvoices >= Number(usage.limit);

  if (invoiceLimitReached) {
    return (
      <div className="min-h-full flex items-center justify-center px-4 py-10" data-testid="generator-limit-reached">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-xl shadow-sm p-7 text-center">
          <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-2xl font-black text-slate-900">{t('limitReached')}</h1>
          <p className="text-sm text-slate-500 mt-2">
            {t('invoiceLimitCopy')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-2 mt-6">
            <Link to="/plan">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">{t('upgradePlan')}</Button>
            </Link>
            <Link to="/history">
              <Button variant="outline">{t('viewHistory')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const previewPanel = (
    <div className="flex flex-col gap-3 p-3 xl:p-4 lg:h-full lg:overflow-y-auto" style={{ background: '#ECECEB' }}>
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400 text-center flex-shrink-0 flex items-center justify-center gap-2">
        <span>{t('livePreview')}</span>
        <button
          type="button"
          onClick={() => setFullscreenPreview(true)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 shadow-sm transition-colors hover:border-orange-300 hover:text-orange-600"
          aria-label={t('fullScreenPreview')}
          title={t('fullScreenPreview')}
          data-testid="fullscreen-preview-button"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div style={{
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        borderRadius: '2px',
        overflow: 'hidden',
        flexShrink: 0,
        width: '100%',
      }}>
        <InvoicePreview invoice={previewInvoice} />
      </div>
    </div>
  );

  return (
    <div className="lg:h-full lg:grid lg:overflow-hidden" style={{ gridTemplateColumns: '52% 48%' }} data-testid="generator-page">

      {/* LEFT — form */}
      <div className="lg:h-full lg:overflow-y-auto lg:border-r border-slate-200 px-4 sm:px-7 py-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight">{t('createGstInvoice')}</h1>
          </div>
          {/* Mobile preview toggle */}
          <button
            className="lg:hidden flex items-center gap-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg px-3 py-2 bg-white shadow-sm flex-shrink-0 mt-1"
            onClick={() => setMobilePreview(v => !v)}
          >
            {mobilePreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {mobilePreview ? t('hidePreview') : t('preview')}
          </button>
        </div>

        {/* Mobile inline preview */}
        {mobilePreview && (
          <div className="lg:hidden rounded-xl overflow-hidden border border-slate-200">
            {previewPanel}
          </div>
        )}

        <BusinessSection business={business} setBusiness={setBusiness} />
        <CustomerSection customer={customer} setCustomer={setCustomer} savedCustomers={savedCustomers} onSaveCustomer={handleSaveCustomer} />
        <LineItems lineItems={lineItems} addLineItem={addLineItem} addMultipleLineItems={addMultipleLineItems} removeLineItem={removeLineItem} removeLineItems={removeLineItems} duplicateLineItem={duplicateLineItem} updateLineItem={updateLineItem} savedItems={savedItems} />
        <TaxSummary taxSummary={invoice.taxSummary} />
        <AdditionalFields notes={notes} setNotes={setNotes} business={business} setBusiness={setBusiness} number={number} setNumber={setNumber} date={date} setDate={setDate} dueDate={dueDate} setDueDate={setDueDate} paymentTerms={paymentTerms} setPaymentTerms={setPaymentTerms} customPaymentTerms={customPaymentTerms} setCustomPaymentTerms={setCustomPaymentTerms} />
        <ActionButtons invoice={invoice} onSaved={handleSaved} onReset={reset} onTogglePreview={() => {}} />

        {/* Mobile bottom padding */}
        <div className="lg:hidden h-6" />
      </div>

      {/* RIGHT — desktop-only preview panel */}
      <div className="hidden lg:block">
        {previewPanel}
      </div>

      {fullscreenPreview && (
        <div className="fixed inset-0 z-[90] bg-slate-950/80 backdrop-blur-sm p-4 sm:p-6" data-testid="fullscreen-preview-modal">
          <div className="mx-auto flex h-full max-w-[1180px] flex-col rounded-xl bg-[#ECECEB] shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-300/70 bg-white px-4 py-3">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">{t('livePreview')}</div>
              <button
                type="button"
                onClick={() => setFullscreenPreview(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                aria-label={t('closePreview')}
                data-testid="close-fullscreen-preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 sm:p-6">
              <div className="mx-auto max-w-[980px] shadow-2xl">
                <InvoicePreview invoice={previewInvoice} />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
